import { Color, Spacing } from '@persona-id/design-tokens';
import { DateTime, Settings } from 'luxon';
import {
  FocusEventHandler,
  KeyboardEventHandler,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { MaskedInput } from 'rsuite';
import Calendar, { CalendarState } from 'rsuite/cjs/Calendar/Calendar';
import { useCalendarState } from 'rsuite/cjs/DatePicker/utils';
import defaultLocale from 'rsuite/locales/default';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import {
  ControlConfig,
  processDateTimeValue,
} from 'dashboard/components/FormControlPilledInput/types';
import { DateTimeValue, ValueType } from 'dashboard/models/graph-query';
import { Z_INDICES } from 'dashboard/styles/variables';
import { paragraphMixins } from 'design-system/mixins/text';
import { Elevation } from 'design-system/variables/effect';

// minimally extracted css required for rendering the calendar
import 'dashboard/components/FormControlPilledInput/rsuite-calendar.css';
import 'dashboard/components/FormControlPilledInput/rsuite-button.css';

interface EditableItemDateTimePickerProps {
  controlConfig: ControlConfig;
  deleteItem: () => void;
  deleteLastItem: () => void;
  inputRef: MutableRefObject<HTMLInputElement>;
  isLast: boolean;
  itemKey: string;
  value: DateTimeValue;
  onValueChange: (value: DateTimeValue) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  isCompact?: boolean;
}

const Container = styled('div')`
  display: inline-block;
  color: black;
`;

const EditableDateTimePicker = styled(MaskedInput).withConfig({
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  shouldForwardProp: (prop: string) => !['isCompact'].includes(prop),
})<{ isCompact: boolean }>`
  ${paragraphMixins.md};
  &::placeholder {
    font-weight: ${({ theme }) => theme.text.paragraph.md.fontWeight};
    color: ${({ theme }) => theme.color.text.tertiary};
  }

  background: none;
  border: none;

  display: inline;
  min-width: 40px;
  width: unset;
  ${({ isCompact }) => {
    if (isCompact) {
      return `
        padding: unset;
        height: 100%;
      `;
    }
    return `padding: ${Spacing[1]} 0`;
  }};
`;

const PickerDateTimeContainer = styled('div')`
  position: absolute;
  width: 360px;
  background: ${Color.PrimaryWhite};
  border-radius: ${BORDER_RADIUSES.rounded};
  box-shadow: ${Elevation.Mid};
  z-index: ${Z_INDICES.dropdown};
`;

// This is by analogy with DateTimeRangeCoordinator in EditableItemDateTimeRangePicker.tsx,
// in order to coordinate the timezones and representations of the Calendar-widget view with
// the text input.
export class DateTimeCoordinator {
  private readonly timezone: string;
  private readonly initialValue: DateTimeValue;
  private readonly _calendarFormat: string;

  public constructor(timezone: string, initialValue: DateTimeValue) {
    // Using an explicit timezone instead of relying on `.local()` greatly simplifies testing.
    this.timezone = timezone;
    this._calendarFormat = 'yyyy-MM-dd HH:mm:ss';
    this.initialValue = initialValue;
  }

  public get calendarFormat(): string {
    return this._calendarFormat;
  }

  public initialDateText(): string {
    const initialMoment = this.initialValue?.moment
      ? DateTime.fromISO(this.initialValue.moment, { zone: this.timezone })
      : undefined;

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return this.formatIfValid(initialMoment, this.initialValue?.['raw-value'] || '');
  }

  public textLength(): number {
    return this._calendarFormat.length;
  }

  public toValue(dateText: string): DateTimeValue {
    const dateAsMoment = DateTime.fromFormat(dateText, this.calendarFormat, {
      zone: this.timezone,
    });

    return processDateTimeValue(
      {
        ...this.initialValue,
        'raw-value': dateAsMoment.isValid ? dateAsMoment.toUTC().toISO() : dateText,
        type: ValueType.DateTime,
      },
      undefined,
      true,
    );
  }

  public formatDateText(date: Date): string {
    // Grab the local time and convert it to UTC
    const localDateTime = DateTime.fromSeconds(
      date.getTime() / 1000 - date.getTimezoneOffset() * 60,
      {
        zone: this.timezone,
      },
    ).toUTC();
    return localDateTime.toFormat(this.calendarFormat);
  }

  public toControl(dateText: string): Date {
    let dateTime = DateTime.fromFormat(dateText, this.calendarFormat, { zone: this.timezone });
    if (!dateTime.isValid) {
      dateTime = DateTime.local({ zone: this.timezone });
    }
    const dateValue = new Date(dateTime.toISO());
    return new Date(dateValue.toLocaleString('en-US', { timeZone: this.timezone }));
  }

  private formatIfValid(moment: DateTime | null, defaultString: string): string {
    return moment && moment.isValid ? moment.toFormat(this.calendarFormat) : defaultString;
  }
}

const EditableItemDateTimePicker = ({
  deleteItem,
  deleteLastItem,
  inputRef,
  isLast,
  itemKey,
  onValueChange,
  value: initialValue,
  // Remove this flag once we've ramped GraphV2 to 100%
  isCompact = false,
}: EditableItemDateTimePickerProps) => {
  const dateTimeCoordinator = useMemo(
    () => new DateTimeCoordinator(Settings.defaultZone.name, initialValue),
    [initialValue, Settings.defaultZone.name],
  );
  // Note: The `value` is in UTC, even though we display everything on the frontend in local time.
  const [value, setValue] = useState<string>(dateTimeCoordinator.initialDateText());
  const [focused, setFocused] = useState<boolean>(false);
  const { calendarState, reset, openMonth, openTime } = useCalendarState();
  const internalRef = useRef<HTMLInputElement>();

  const handleChange = (value: string) => {
    setValue(value);
  };

  // NOTE: This is used for the keyboard entry of dates (and not for the Calendar control entry of dates).
  const commitValue = () => {
    onValueChange(dateTimeCoordinator.toValue(value));

    // TODO: See if we can remove this by changing 'defaultValue' and
    // making use of useEffect
    setValue('');
  };

  const handleKeyPress: KeyboardEventHandler = (e) => {
    if (e.key === 'Enter' && value.trim() !== '') {
      e.preventDefault();
      commitValue();
    }
  };

  const handleKeyDown: KeyboardEventHandler = (e) => {
    if (e.key === 'Tab' && value.trim() !== '') {
      e.preventDefault();

      commitValue();
    }

    if (e.key === 'Backspace' && value.trim() === '') {
      if (isLast) {
        deleteLastItem();
      } else {
        deleteItem();
      }
    }
  };

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = () => {
    setFocused(false);

    if (initialValue['raw-value'] !== value) {
      commitValue();
    }
  };

  useEffect(() => {
    internalRef.current?.setAttribute('size', JSON.stringify(dateTimeCoordinator.textLength() + 1));
  }, [dateTimeCoordinator]);

  const handleControlDrivenDateChange = useCallback(
    (value: Date) => {
      setValue(dateTimeCoordinator.formatDateText(value));
      // Focus the input so the user can still type
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      internalRef.current.focus();
    },
    [setValue],
  );

  const handleToggleMonthDropdown = useCallback(() => {
    if (calendarState === CalendarState.DROP_MONTH) {
      reset();
    } else {
      openMonth();
    }
  }, [calendarState, openMonth, reset]);

  const handleToggleTimeDropdown = useCallback(() => {
    if (calendarState === CalendarState.DROP_TIME) {
      reset();
    } else {
      openTime();
    }
  }, [calendarState, openTime, reset]);

  return (
    <Container>
      <OutsideClickHandler onOutsideClick={handleBlur}>
        <EditableDateTimePicker
          defaultValue={value}
          guide={value[0] !== '{'}
          inputRef={(input: HTMLInputElement) => {
            internalRef.current = input;
            inputRef.current = input;
          }}
          key={itemKey}
          mask={(value: string) => {
            if (value[0] === '{') {
              return false;
            } else {
              return [
                /\d/,
                /\d/,
                /\d/,
                /\d/,
                '-',
                /\d/,
                /\d/,
                '-',
                /\d/,
                /\d/,
                ' ',
                /\d/,
                /\d/,
                ':',
                /\d/,
                /\d/,
                ':',
                /\d/,
                /\d/,
              ];
            }
          }}
          placeholder={dateTimeCoordinator.calendarFormat}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyPress={handleKeyPress}
          onKeyDown={handleKeyDown}
          value={value}
          isCompact={isCompact}
        />
        {focused && (!value || value[0] !== '{') && (
          <PickerDateTimeContainer>
            <div>
              <Calendar
                calendarDate={dateTimeCoordinator.toControl(value)}
                calendarState={calendarState}
                format={dateTimeCoordinator.calendarFormat}
                inline
                locale={defaultLocale.Calendar}
                onChangePageTime={handleControlDrivenDateChange}
                onChangePageDate={handleControlDrivenDateChange}
                onMoveBackward={handleControlDrivenDateChange}
                onMoveForward={handleControlDrivenDateChange}
                onSelect={handleControlDrivenDateChange}
                onToggleMonthDropdown={handleToggleMonthDropdown}
                onToggleTimeDropdown={handleToggleTimeDropdown}
              />
            </div>
          </PickerDateTimeContainer>
        )}
      </OutsideClickHandler>
    </Container>
  );
};

export default EditableItemDateTimePicker;
